import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import { millesimeHasEveryDay } from "@src/conformite/conformite.helper";

/**
 * Schéma de validation pour la Garantie Financière IAS
 * pour les millésimes < 2026 (comportement historique)
 */
export const createGarantieFinanciereIasSchema2025 = (millesime: number) =>
  zod.object({
    millesime: zod.number(),
    files: zod
      .array(
        zod.object({
          pathname: zod.string().optional(),
          filename: zod.string().optional(),
          startDate: zod.date(),
          endDate: zod.date(),
          file: zod.instanceof(File).optional(),
          isValid: zod.boolean(),
        })
      )
      .min(1)
      .refine((files) => millesimeHasEveryDay(millesime, files), {
        message: "Vos attestations doivent couvrir l'année entière",
        path: ["0.startDate"],
      }),
    nonDiffusable: zod
      .object({
        fileNonDiffusable: zod.nullable(
          zod.object({
            pathname: zod.string().optional(),
            filename: zod.string().optional(),
            isValid: zod.boolean(),
            file: zod.instanceof(File).optional(),
          })
        ),
        noCommunication: zod.boolean().nullable(),
      })
      .refine(
        (data) =>
          !data.noCommunication ||
          (data.noCommunication && data.fileNonDiffusable),
        {
          message:
            "Vous devez fournir une attestation de la part de votre assureur",
          path: ["noCommunication"],
        }
      ),
    infoGarantie: zod
      .object({
        primesEncaisseesDehorsMandat: zod.number(),
        primesEncaisseesMontant: zod.number(),
        indemniteSinistreDehorsMandat: zod.number(),
        indemniteSinistreMontant: zod.number(),
        montantGarantie: zod.number(),
        montantFondRoulement: zod.number(),
      })
      .superRefine((data, ctx) => {
        const garantieFinanciereSimulatedWithoutFondRoulement =
          (data.primesEncaisseesDehorsMandat +
            data.indemniteSinistreDehorsMandat) /
          6;
        const garantieFinanciereSimulated =
          garantieFinanciereSimulatedWithoutFondRoulement +
          data.montantFondRoulement;
        const garantieFinanciereSimulatedRounded =
          Math.round(garantieFinanciereSimulated * 100) / 100;

        if (garantieFinanciereSimulatedRounded <= 115_000) {
          if (data.montantGarantie < 115_000) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message:
                "Le montant de la garantie financière doit être supérieur ou égal à 115 000 €",
              path: ["montantGarantie"],
            });
          }
        } else if (data.montantGarantie < garantieFinanciereSimulatedRounded) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: `Le montant de la garantie financière doit être supérieur ou égal à ${garantieFinanciereSimulatedRounded} €`,
            path: ["montantGarantie"],
          });
        }
      }),
    encaissementFonds: zod.boolean(),
    hasExpertComptable: zod.boolean(),
    couvertParMandat: zod.boolean(),
  });

export type GarantieFinanciereIasFormValues2025 = zod.infer<
  ReturnType<typeof createGarantieFinanciereIasSchema2025>
>;
